// File: src/components/bio-logs/config.js

import React, { useMemo, useState, useEffect, lazy, useCallback, Suspense, useRef, useContext } from 'react';
import { Typography, ListItemIcon, ListItemText, Box, ButtonBase, Collapse, IconButton, Tooltip, Dialog, DialogContent, CircularProgress } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DynamicIcon from '../common/DynamicIcon';
import * as bioLogsApi from '../../services/bioLogsApi';
import { getTrackableDefinitions } from '../../services/trackablesApi';
import { DomainObjectManager } from '@platform/core/src/components/domain';
import { ScrollableContainer } from '@platform/core/src/components/common/ScrollableTabs';
import { SidebarContext } from '@platform/core/src/components/layout/RightSidebar';
import { useRegisterModalBackHandler } from '@platform/core/src/context/ModalBackHandlerContext';

const BioLogForm = lazy(() => import('./BioLogForm'));
const BioLogView = lazy(() => import('./BioLogView'));
const TrackableDefinitionForm = lazy(() => import('./TrackableDefinitionForm'));

const BIO_LOG_TYPE_CONFIG = {
  WEIGHT: { icon: 'weight', color: '#4CAF50', label: 'Weight' },
  SLEEP: { icon: 'sleep', color: '#5C6BC0', label: 'Sleep' },
  NUTRITION: { icon: 'nutrition', color: '#FF7043', label: 'Nutrition' },
  MOOD: { icon: 'mood', color: '#FFB300', label: 'Mood' },
  MEASUREMENT: { icon: 'measure', color: '#26A69A', label: 'Measure' },
};

const DEFAULT_COLORS = [
  '#7C4DFF', '#00BCD4', '#E91E63', '#8BC34A', '#FF5722',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];

let _trackableDefsCache = null;
let _trackableDefsPromise = null;

/**
 * Format total minutes into H:m string
 * @param {number} totalMinutes 
 * @returns {string} e.g. "1h 30m"
 */
const formatDurationValue = (totalMinutes) => {
  if (totalMinutes === undefined || totalMinutes === null || isNaN(totalMinutes)) return '';
  const mins = Math.round(totalMinutes);
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

/**
 * Check if a key implies a duration field
 */
const isDurationKey = (key) => {
  const lowerKey = key.toLowerCase();
  return lowerKey.includes('duration') || lowerKey.includes('time_spent') || lowerKey.includes('elapsed');
};

/**
 * Format sleep duration for display (Specific to Sleep type structure)
 * @param {object} data - Sleep log data
 * @returns {string} Formatted duration string like "8h 15m"
 */
const formatSleepDuration = (data) => {
  if (data?.duration_minutes !== undefined && data?.duration_minutes !== null) {
    return formatDurationValue(data.duration_minutes);
  }
  if (data?.hours !== undefined && data?.hours !== null) {
    const hoursVal = parseFloat(data.hours);
    if (!isNaN(hoursVal)) {
      return formatDurationValue(hoursVal * 60);
    }
  }
  return '--';
};

const useTrackableDefinitions = (requiredType) => {
  const [defs, setDefs] = useState(_trackableDefsCache || []);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      const isStandard = requiredType && BIO_LOG_TYPE_CONFIG[requiredType.toUpperCase()];
      const cacheExists = _trackableDefsCache !== null;
      const typeMissing = requiredType && cacheExists && !_trackableDefsCache.find(d => d.key === requiredType);

      if (typeMissing && !isStandard) {
        _trackableDefsCache = null;
        _trackableDefsPromise = null;
      }

      if (_trackableDefsCache) {
        if (isActive) setDefs(_trackableDefsCache);
        return;
      }

      if (!_trackableDefsPromise) {
        _trackableDefsPromise = getTrackableDefinitions().then(data => {
          _trackableDefsCache = data;
          return data;
        }).catch(err => {
          console.warn("Failed to load trackable definitions for icons", err);
          return [];
        });
      }

      const data = await _trackableDefsPromise;
      if (isActive) setDefs(data);
    };

    load();
    return () => { isActive = false; };
  }, [requiredType]);

  return defs;
};

const BioLogListItemContent = ({ item }) => {
  const type = item.log_type || 'WEIGHT';
  const definitions = useTrackableDefinitions(type);
  
  const upperType = type.toUpperCase();
  const standardConfig = BIO_LOG_TYPE_CONFIG[upperType];
  const definition = definitions.find(d => d.key === type);
  
  const config = standardConfig || {
    icon: definition?.icon || item.icon || 'note',
    color: DEFAULT_COLORS[type.charCodeAt(0) % DEFAULT_COLORS.length],
    label: definition?.display_name || type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
  };

  const dateObj = item.date ? new Date(item.date) : new Date();
  const dateString = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  let primaryText = config.label;
  let secondaryText = '';

  if (upperType === 'WEIGHT') {
    secondaryText = `${item.data?.value || '--'} ${item.data?.unit || 'lbs'}`.trim();
  } else if (upperType === 'SLEEP') {
    const parts = [];
    const durationStr = formatSleepDuration(item.data);
    if (durationStr !== '--') parts.push(durationStr);
    if (item.data?.quality) parts.push(`Quality: ${item.data.quality}/5`);
    secondaryText = parts.join(' · ') || 'No data';
  } else if (upperType === 'MOOD') {
    const feelings = item.data?.feelings || (item.data?.feeling ? [item.data.feeling] : []);
    const parts = [];
    if (feelings.length > 0) {
      parts.push(feelings.map(f => f.charAt(0).toUpperCase() + f.slice(1)).slice(0, 2).join(', '));
    }
    if (item.data?.energy) parts.push(`Energy: ${item.data.energy}/5`);
    secondaryText = parts.join(' · ') || 'No data';
  } else if (upperType === 'NUTRITION') {
    const parts = [];
    if (item.data?.calories) parts.push(`${item.data.calories} kcal`);
    if (item.data?.protein) parts.push(`${item.data.protein}g protein`);
    secondaryText = parts.join(' · ') || 'No data';
  } else if (upperType === 'MEASUREMENT') {
    const unit = item.data?.unit || 'in';
    if (item.data?.measurements && typeof item.data.measurements === 'object') {
      const entries = Object.entries(item.data.measurements).filter(
        ([_, value]) => value !== '' && value !== null && value !== undefined
      );
      if (entries.length === 0) {
        secondaryText = 'No data';
      } else if (entries.length === 1) {
        const [part, value] = entries[0];
        secondaryText = `${part.charAt(0).toUpperCase() + part.slice(1)}: ${value} ${unit}`;
      } else {
        const displays = entries.slice(0, 2).map(([part, value]) => 
          `${part.charAt(0).toUpperCase() + part.slice(1)}: ${value}`
        );
        secondaryText = displays.join(' · ') + ` ${unit}`;
      }
    } else {
      secondaryText = item.data?.part 
        ? `${item.data.part}: ${item.data?.value || '--'} ${unit}`
        : 'No data';
    }
  } else {
    // Generic fallback for custom types
    if (item.data) {
      const keys = Object.keys(item.data).filter(k => !k.endsWith('_unit'));
      const fieldDisplays = [];
      
      keys.forEach(key => {
        const val = item.data[key];
        if (val === undefined || val === null || val === '') return;
        
        const unit = item.data[`${key}_unit`] || '';
        
        if (typeof val === 'boolean') {
          const label = key.replace(/_/g, ' ');
          const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
          fieldDisplays.push(`${capitalizedLabel}: ${val ? 'Yes' : 'No'}`);
        } else if (typeof val !== 'object') {
          // Check if it's a duration field
          if (typeof val === 'number' && isDurationKey(key)) {
             fieldDisplays.push(formatDurationValue(val));
          } else {
             fieldDisplays.push(`${val}${unit ? ` ${unit}` : ''}`);
          }
        }
      });
      
      secondaryText = fieldDisplays.length > 0 
        ? fieldDisplays.slice(0, 3).join(' · ') 
        : 'No data';
    }
  }

  return (
    <>
      <ListItemIcon sx={{ minWidth: 40, mr: 1 }}>
        <Box
          sx={{
            p: 0.75,
            borderRadius: '50%',
            bgcolor: alpha(config.color, 0.1),
            color: config.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DynamicIcon icon={config.icon} fontSize="small" />
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>
              {primaryText}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {dateString}
            </Typography>
          </Box>
        }
        secondary={
          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.2 }}>
            {secondaryText}
          </Typography>
        }
        sx={{ m: 0 }}
      />
    </>
  );
};

const BioLogFilter = ({ selectedTypes, onToggle }) => {
  const definitions = useTrackableDefinitions(null);
  
  const allTypes = useMemo(() => {
    const types = [];
    // Add standard types
    Object.entries(BIO_LOG_TYPE_CONFIG).forEach(([key, config]) => {
      types.push({ key: key, ...config });
    });
    
    // Add custom types from definitions
    definitions.forEach(def => {
      if (!types.find(t => t.key === def.key.toUpperCase())) {
        const index = types.length;
        types.push({ 
          key: def.key, 
          label: def.display_name, 
          icon: def.icon,
          color: DEFAULT_COLORS[index % DEFAULT_COLORS.length]
        });
      }
    });
    
    return types;
  }, [definitions]);

  return (
    <ScrollableContainer
      sx={{
        gap: 1.5,
        p: 2,
        pb: 1,
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
      }}
    >
      {allTypes.map(type => {
        const isSelected = selectedTypes.includes(type.key);
        return (
          <ButtonBase
            key={type.key}
            onClick={() => onToggle(type.key)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              opacity: selectedTypes.length === 0 || isSelected ? 1 : 0.4,
              transition: 'opacity 0.2s',
              minWidth: 60
            }}
          >
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: isSelected ? type.color : alpha(type.color, 0.1),
              color: isSelected ? '#fff' : type.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.5,
              border: isSelected ? `2px solid ${type.color}` : '2px solid transparent',
              transition: 'all 0.2s'
            }}>
              <DynamicIcon icon={type.icon} />
            </Box>
            <Typography variant="caption" sx={{ 
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'text.primary' : 'text.secondary',
              whiteSpace: 'nowrap'
            }}>
              {type.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </ScrollableContainer>
  );
};

export const bioLogConfig = {
  id: 'bioLogs',
  resourceType: 'bio_log',
  singularName: 'Bio Log',
  pluralName: 'Bio Logs',
  createHeaderDescription: false,
  icon: MonitorWeightIcon,
  searchable: false,
  modalSize: 'sm',
  renderListItemContent: BioLogListItemContent,
  getDisplayName: (item) => {
    const type = item.log_type;
    const date = item.date ? new Date(item.date).toLocaleDateString() : '';
    let details = '';
    
    const upperType = type?.toUpperCase();
    
    if (upperType === 'WEIGHT') details = `${item.data?.value || '--'} ${item.data?.unit || ''}`;
    else if (upperType === 'SLEEP') details = formatSleepDuration(item.data);
    else if (upperType === 'MOOD') {
      const feelings = item.data?.feelings || (item.data?.feeling ? [item.data.feeling] : []);
      details = feelings.length > 0 ? feelings.join(', ') : 'logged';
    }
    else if (upperType === 'NUTRITION') details = `${item.data?.calories || '--'} kcal`;
    else if (upperType === 'MEASUREMENT') {
      if (item.data?.measurements) {
        const count = Object.keys(item.data.measurements).filter(k => item.data.measurements[k]).length;
        details = `${count} measurements`;
      } else {
        details = `${item.data?.part}: ${item.data?.value || '--'}`;
      }
    }
    else if (item.data) {
      const keys = Object.keys(item.data).filter(k => !k.endsWith('_unit'));
      const vals = [];
      keys.forEach(key => {
        const val = item.data[key];
        if (typeof val === 'boolean') {
          vals.push(`${key.replace(/_/g, ' ')}: ${val ? 'Yes' : 'No'}`);
        } else if (val !== undefined && val !== null && val !== '' && typeof val !== 'object') {
          if (typeof val === 'number' && isDurationKey(key)) {
             vals.push(formatDurationValue(val));
          } else {
             const unit = item.data[`${key}_unit`] || '';
             vals.push(`${val}${unit ? ` ${unit}` : ''}`);
          }
        }
      });
      details = vals.length > 0 ? vals.slice(0, 2).join(', ') : 'logged';
    } else {
      details = 'logged';
    }
    
    return `${type} (${details}) - ${date}`;
  },
  sortOptions: [
    {
      label: 'Date',
      value: 'date',
      sorter: (a, b) => new Date(b.date) - new Date(a.date),
    }
  ],
  getInitialState: (data) => ({
    id: data?.id || null,
    log_type: data?.log_type || 'WEIGHT',
    date: data?.date || new Date().toISOString(),
    data: data?.data || {},
    user_id: data?.user_id,
    _isPartial: data?._isPartial || false,
  }),
  api: {
    getAll: () => bioLogsApi.getBioLogs(),
    create: bioLogsApi.createBioLog,
    update: bioLogsApi.updateBioLog,
    delete: bioLogsApi.deleteBioLog,
    getById: bioLogsApi.getBioLogById,
  },
  FormComponent: BioLogForm,
  ViewComponent: BioLogView,
};

export const BioLogSidebarPanel = () => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [filterRefreshKey, setFilterRefreshKey] = useState(0);
  const { setHeaderAction } = useContext(SidebarContext);

  useRegisterModalBackHandler(() => setIsManageOpen(false), isManageOpen);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction(
        <Tooltip title="Manage Trackers">
          <IconButton size="small" onClick={() => setIsManageOpen(true)}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    return () => {
      if (setHeaderAction) setHeaderAction(null);
    };
  }, [setHeaderAction]);

  const handleToggle = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      }
      return [...prev, type];
    });
  };

  const filterLogs = useCallback((items) => {
    if (selectedTypes.length === 0) return items;
    return items.filter(item => {
      const itemType = (item.log_type || '').toUpperCase();
      return selectedTypes.some(t => t.toUpperCase() === itemType);
    });
  }, [selectedTypes]);

  const dynamicConfig = useMemo(() => ({
    ...bioLogConfig,
    searchFilter: (item, query) => {
      if (!query) return true;
      const q = query.toLowerCase();
      const typeMatch = (item.log_type || '').toLowerCase().includes(q);
      let dataMatch = false;
      if (item.data) {
        dataMatch = Object.values(item.data).some(val => 
          String(val).toLowerCase().includes(q)
        );
      }
      return typeMatch || dataMatch;
    }
  }), []);

  const handleDefinitionsChange = () => {
    _trackableDefsCache = null;
    _trackableDefsPromise = null;
    setFilterRefreshKey(prev => prev + 1);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BioLogFilter key={filterRefreshKey} selectedTypes={selectedTypes} onToggle={handleToggle} />
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <DomainObjectManager
          config={dynamicConfig}
          hideHeader={true}
          customFilter={filterLogs}
        />
      </Box>
      <Dialog
        open={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ p: 0 }}>
          <Suspense fallback={<Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}>
            <Box sx={{ p: 3 }}>
              <TrackableDefinitionForm 
                onCancel={() => setIsManageOpen(false)}
                onDefinitionsChange={handleDefinitionsChange}
              />
            </Box>
          </Suspense>
        </DialogContent>
      </Dialog>
    </Box>
  );
};